import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface CTAButtonProps {
  href?: string;
  onClick?: () => void;
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  icon?: string;
  iconPosition?: 'left' | 'right';
  className?: string;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
}

export default function CTAButton({
  href,
  onClick,
  children,
  variant = 'primary',
  size = 'md',
  icon,
  iconPosition = 'right',
  className = '',
  disabled = false,
  type = 'button'
}: CTAButtonProps) {
  // Base classes with focus states for accessibility
  const baseClasses = 'font-sans text-nav font-medium rounded-sm transition-all duration-300 inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 focus:outline-none';

  const variantClasses = {
    primary: 'bg-primary text-primary-foreground hover:shadow-neon-cyan hover:scale-105 focus:shadow-neon-cyan-intense focus:scale-105',
    secondary: 'bg-secondary text-secondary-foreground hover:shadow-neon-magenta hover:scale-105 focus:shadow-neon-magenta focus:scale-105',
    outline: 'bg-transparent border-2 border-primary text-primary hover:bg-primary/10 focus:bg-primary/10'
  };

  // Mobile-first touch-friendly sizes (minimum 44px height for accessibility)
  const sizeClasses = {
    sm: 'px-4 py-2.5 min-h-[44px]',
    md: 'px-6 py-3 min-h-[48px]',
    lg: 'px-8 py-4 min-h-[56px]'
  };

  const classes = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`;

  const iconElement = icon && (
    <Icon name={icon as any} size={size === 'sm' ? 16 : size === 'lg' ? 24 : 20} />
  );

  const content = (
    <>
      {icon && iconPosition === 'left' && iconElement}
      {children}
      {icon && iconPosition === 'right' && iconElement}
    </>
  );

  if (href && !disabled) {
    return (
      <Link href={href} className={classes}>
        {content}
      </Link>
    );
  }

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={classes}
    >
      {content}
    </button>
  );
}

